import { useEffect, useRef } from "react";

import { View } from "./View";
import {
  useApiErrors,
  useAuth,
  useCounter,
  useMerchantApiMutation,
  useQueryString,
} from "../../hooks";
import {
  IAcceptTeamInvitationCodeRequest,
  IStaffDepartment,
} from "../../interfaces-and-types";
import { getApiErrorsByFieldName } from "../../utils";
import { useNavigate } from "react-router-dom";
import {
  acceptTeamInvitationConstants,
  accountConfirmationConstants,
} from "../../constants";

export interface IAcceptTeamInvitationViewProps {
  closeModal: () => void;
  invitationCode: string;
  isLoadingAcceptTeamInvitation: boolean;
  isErrorAcceptTeamInvitation: boolean;
  emailConfirmationSuccessful: boolean;
  redirectToLoginCountdown: number;
  errorMessage: string;
}

export const AcceptTeamInvitation = () => {
  const navigate = useNavigate();
  const { setStaffDepartments } = useAuth();

  // get the form values from the url
  const query = useQueryString();

  const emailQueryString = query.get(
    accountConfirmationConstants.emailQueryStringName
  );
  const invitationCodeQueryString = query.get(
    acceptTeamInvitationConstants.invitationCodeQueryStringName
  );

  const {
    isLoading: isLoadingAcceptTeamInvitation,
    isSuccess: isSuccessAcceptTeamInvitation,
    isError: isErrorAcceptTeamInvitation,
    data: userMerchantAccountStaffDeptResponse,
    mutate: acceptTeamInvitation,
  } = useMerchantApiMutation<
    IAcceptTeamInvitationCodeRequest,
    IStaffDepartment[]
  >("acceptTeamInvitation").usePostMutation;

  useEffect(() => {
    if (isSuccessAcceptTeamInvitation) {
      setStaffDepartments(userMerchantAccountStaffDeptResponse.data); // set the new staff departments in the header
    }
  }, [isSuccessAcceptTeamInvitation]);

  const { apiErrors } = useApiErrors();

  const redirectToLoginCountdown = useCounter({
    isActive: isSuccessAcceptTeamInvitation || isErrorAcceptTeamInvitation,
    initialStartCount: isErrorAcceptTeamInvitation ? 3 : 5,
    stopValue: 0,
    countDirection: "DOWN",
  });

  useEffect(() => {
    // console.log("redirectToLoginCountdown value: ", redirectToLoginCountdown);

    if (redirectToLoginCountdown === 0) navigate("/");
  }, [redirectToLoginCountdown]);

  let timeoutId = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const allQueryStringValuesRetrieved =
      !!emailQueryString && !!invitationCodeQueryString;

    if (allQueryStringValuesRetrieved) {
      timeoutId.current = setTimeout(
        () =>
          acceptTeamInvitation({
            // email: emailQueryString,
            invitation_code: invitationCodeQueryString,
          }),
        1000
      );
    }

    return () => timeoutId && clearTimeout(timeoutId.current);
  }, [emailQueryString, invitationCodeQueryString]);

  const errorMessage =
    getApiErrorsByFieldName(
      accountConfirmationConstants.emailQueryStringName,
      apiErrors
    ) ||
    getApiErrorsByFieldName(
      accountConfirmationConstants.confirmationCodeQueryStringName,
      apiErrors
    ) ||
    getApiErrorsByFieldName(
      accountConfirmationConstants.merchantIdQueryStringName,
      apiErrors
    ) ||
    "";

  const closeModal = () => navigate("/");

  return (
    <View
      closeModal={closeModal}
      invitationCode={invitationCodeQueryString || ""}
      isLoadingAcceptTeamInvitation={isLoadingAcceptTeamInvitation}
      isErrorAcceptTeamInvitation={isErrorAcceptTeamInvitation}
      emailConfirmationSuccessful={isSuccessAcceptTeamInvitation}
      redirectToLoginCountdown={redirectToLoginCountdown}
      errorMessage={errorMessage}
    />
  );
};
